/**
 * Handles all modals
 * @module modal
 */

import $ from 'jquery';
import Keyboard from './keyboard';
import registerJQueryPlugin, { noopActions } from '../lib/register-jquery-plugin';

// Expose the function as a jQuery plugin for ease of use
export const PLUGIN_NAME = 'modal';
export const DEFAULTS = {
  url: '',
  type: '',
  open: null,
  data: '',
  returnFocus: '',
  cache: true,
  closeButton: true,
  overlayClose: true,
  triggerOpen: false,
  triggerElement: '',
  padding: true,
  onOpen: () => {},
  onComplete: () => {},
  onLoad: () => {},
  onCleanup: () => {},
  onClose: () => {},
  appendTo: 'body',
};
const unlockBody = () => {
  $('body').css({ overflow: '' });
};
const lockBody = () => {
  $('body').css({ overflow: 'hidden' });
};
/**
 * Check if options.url is define, if it is create an empty modal
 * for ajax content. Otherwise, assign with options.open or $el's data attribute
 * @return {jQuery} modal element
 */
const appendModal = () => {
  const $modalHTML = $('<div/>', { id: 'modal', class: 'modal' });
  return $modalHTML;
};
const hasHash = ($el) => {
  const href = $el.attr('href');
  if (href) {
    // Double check this logic not sure if this is working correctly - maurice
    if (href.toLowerCase().indexOf('#') === -1) {
      return href;
    }
  }
  return false;
};
/**
 * Get Modal
 * Target modal via options.open / data-open(legacy) / href(accessibility)
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {jQuery} modal element
 */
const getModal = ($el, options = {}) => {
  let $modal;
  if (options.open) {
    $modal = $(`#${options.open}`);
  } else if ($el.attr('href') && !hasHash($el)) {
    $modal = $($el.attr('href'));
  } else if (options.url || hasHash($el)) {
    if ($('#modal').length) {
      $modal = $('#modal');
    } else {
      $modal = appendModal();
    }
  } else if ($el.data('open')) {
    $modal = $(`#${$el.data('open')}`);
  }

  return $modal;
};
/**
 * Merges together all the options and the various conditions in which they
 * can be set.
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Immutable version of the options
 */
const mergeOptions = ($el, options = {}) => {
  // Assign $modal to modal element either by options {} or $el's data attribute
  const $modal = getModal($el, options);
  let opts = {};
  if (!$modal) return false;
  opts = $.extend({}, DEFAULTS, $el.data(), options);
  if (!options.url) {
    opts = $.extend({}, DEFAULTS, $modal.data(), $el.data(), options);
  }
  if (hasHash($el)) {
    opts.url = hasHash($el);
  }
  return opts;
};

/**
 * Check if options.url is define, if it is create an empty modal
 * otherwise target options.open or $el's data attribute
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {jQuery} modal element
 */
const setModal = ($el, options = {}) => {
  // Assign $modal to modal element either by options {} or $el's data attribute
  const $modal = getModal($el, options);
  $modal.appendTo(options.appendTo);
  return $modal;
};
/**
 * @return {jQuery} modal overlay element
 */
const appendModalOverlay = () => {
  if (!$('.modal-overlay').length) {
    $('<div class="modal-overlay"></div>').appendTo('body');
  }
  return $('.modal-overlay');
};
/**
 * Check if options.url is define, if it is create an empty modal
 * otherwise target options.open or $el's data attribute
 * @param  {jQuery} $el - modal element
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {jQuery} modal content element
 */
const wrapModalContent = ($el, options = {}) => {
  if (!$el.find('.modal__content').length) {
    $el.wrapInner(
      '<div role="dialog" tabindex="-1" class="modal__content"></div>',
    );
  }
  if (!options.padding) {
    $el.find('.modal__content').addClass('u-padding-a--none');
  }
  return $el.find('.modal__content');
};
/**
 * Adjust and set classes and options for appropriate modal type
 * @param  {jQuery} $el - modal element
 * @param  {string} type - modifier
 */
const addModalModifiers = ($el, type) => {
  const modalTypeClass = `modal--${type}`;
  if (type) {
    $el.addClass(modalTypeClass);
  }
};
/**
 * Adjust and set classes and options for appropriate modal type
 * @param  {jQuery} $el - modal element
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Immutable modal settings
 */
const adjustToModalType = ($el, options = {}) => {
  let typeAdjust = {
    pageScroll: false,
    overlay: true,
  };
  addModalModifiers($el, options.type);
  switch (options.type) {
    case 'notification':
      typeAdjust = {
        pageScroll: true,
        overlay: false,
      };
      break;
    default:
  }
  return typeAdjust;
};
/**
 * Remove active class on elements
 * @param  {rest}
 */
const removeActiveClass = (...args) => {
  args.forEach(($el) => {
    $el.removeClass('is-active');
  });
};
/**
 * @param  {jQuery} $el - modal element
 */
const removeModalModifiers = ($el) => {
  const classArray = Array.from($el[0].classList);
  const modifers = classArray.filter(modalClass => modalClass.includes('--'));
  modifers.forEach((modClass) => {
    $el.removeClass(modClass);
  });
};
/**
 * Reset modal for next ajax loaded content
 */
const resetAjaxModal = () => {
  const $ajaxModal = $('#modal');
  if ($ajaxModal.length) {
    removeModalModifiers($ajaxModal);
    $ajaxModal
      .find('.modal__content *:not(".modal__close, .modal__close *")')
      .remove();
  }
};
/**
 * Ensure one modal is open at a time
 * @param  {jQuery} $modal
 * @param  {jQuery} $overlay
 */
const closeAllModals = () => {
  clearTimeout(window.modalCloseTimeout);
  resetAjaxModal();
  removeActiveClass($('.modal-overlay'), $('.modal'), $('.modal-blur'));
  unlockBody();
};
/**
 * Add active class on elements
 * @param  {rest}
 */
const addActiveClass = (...args) => {
  // Delay to allow for css animation
  setTimeout(() => {
    args.forEach(($el) => {
      $el.addClass('is-active');
    });
  }, 1);
};
/**
 * Callback after css animation
 * @param {jQuery} $el - css animated element
 */
const afterAnimation = ($el, cb) => {
  const transitionDuration =
    $el.css('transition-duration').replace('s', '') * 1000;
  setTimeout(cb, transitionDuration);
};
/**
 * Return to last focusable element
 * @param  {jQuery} $content
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const returnToLastFocus = ($content, options = {}) => {
  if (options.returnFocus) {
    Keyboard.releaseFocus($content, $(options.returnFocus));
  } else {
    Keyboard.releaseFocus($content, $(document).data('lastFocus'));
  }
};
/**
 * Handles the click event to close the modal
 * @param  {event} event
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @param  {Function} deregister - teardown method from register jquery plugin;
 */
const closeModal = (
  evt,
  $el,
  $modal,
  $content,
  $overlay,
  typeAdjust,
  options = {},
) => {
  clearTimeout(window.modalCloseTimeout);
  if (evt) {
    evt.preventDefault();
  }

  if (!typeAdjust.pageScroll) {
    unlockBody();
  }

  removeActiveClass($modal);
  removeActiveClass($overlay);
  removeActiveClass($('.modal-blur'));

  options.onCleanup({ target: $el, options });
  afterAnimation($content, () => {
    options.onClose({ target: $el, options });
    if (options.url) {
      resetAjaxModal();
    }
    returnToLastFocus($content, options);
  });

  // to ensure the event isn't get bound multiple times
  $content.off('mousedown.modal');
  if (options.overlayClose) {
    // to ensure the event isn't get bound multiple times
    $modal.off('mousedown');
  }
};
/**
 * Set timeout for notification modal
 * @param  {jQuery} $el
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 */
const setNotificationDuration = (
  $el,
  $modal,
  $content,
  $overlay,
  typeAdjust,
  options,
) => {
  // Set Timer base on number of words
  const numWords = $content
    .text()
    .replace(/^[\s,.;]+/, '')
    .replace(/[\s,.;]+$/, '')
    .split(/[\s,.;]+/).length;
  const minDuration = 3000;
  let duration = numWords * 500;
  if (duration < minDuration) {
    duration = minDuration;
  }
  window.modalCloseTimeout = setTimeout(() => {
    closeModal(
      false,
      $el,
      $modal,
      $content,
      $overlay,
      typeAdjust,
      options,
    );
  }, duration);
};
/**
 * Add aria-labelledby to modal content for accessible
 * audible title that corresponds with modal's heading
* @param  {jQuery} $content
 */
const addHedAria = ($content) => {
  let $modalHed = $($content.find('.modal__hed'));
  if (!$content.find('.modal__hed').length) {
    $modalHed = $content.find('.modal__focus-element');
  }
  if ($modalHed.length && !$modalHed.attr('id')) {
    const modalHedID = `modal-hed-${Math.ceil(Math.random() * 10000000000)}`;
    $content.attr('aria-labelledby', modalHedID);
    $modalHed.attr('id', modalHedID).attr('tabindex', '-1');
  }
};
/**
 * Save last focus element that's not inside modal
 * @param  {jQuery} $el - last focusable element
 */
const saveLastFocus = ($el) => {
  if (!$el.parents('.modal__content').length) {
    $(document).data('lastFocus', $el);
  }
};
/**
 * Give each <a> a unique href value, in order to avoid users hearing
 * "visited" on links they've yet to visit.
* @param  {jQuery} $modal
 */
const uniqueCloseHref = ($modal) => {
  $modal.find('.js-modal-close').each(function eachCloseLink() {
    const $closeLink = $(this);
    if (!($closeLink.is('button') || $closeLink.is('a'))) {
      throw new Error('Modal close triggers should be a button or a link.');
    }

    if ($closeLink.is('a')) {
      $closeLink.attr(
        'href',
        `#modal-uid-${Math.ceil(Math.random() * 10000000000)}`,
      );
    }
  });
};
/**
 * Open modal
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @param  {Function} deregister - teardown method from register jquery plugin;
 */
const openModal = (
  $el,
  $modal,
  $content,
  $overlay,
  typeAdjust,
  options = {},
  deregister,
) => {
  options.onOpen({ target: $el, options });
  addActiveClass($modal);
  addHedAria($content);
  saveLastFocus($el);
  uniqueCloseHref($modal);
  $content.scrollTop(0);
  if (typeAdjust.overlay) {
    addActiveClass($overlay);
    addActiveClass($('.modal-blur'));
  }
  if (!typeAdjust.pageScroll) {
    lockBody();
  }
  if (options.type === 'notification') {
    setNotificationDuration(
      $el,
      $modal,
      $content,
      $overlay,
      typeAdjust,
      options,
      deregister,
    );
  }

  // Shift and trap focus to modal after animation
  afterAnimation($content, () => {
    Keyboard.trapFocus($content);
    if ($content.find('.modal__hed').length) {
      $content.find('.modal__hed').focus();
    } else {
      $content.find('.modal__focus-element').focus();
    }
    options.onComplete({ target: $el, options });
  });
};
/**
  * @param  {jQuery} evt - key event
  * @param  {jQuery} $el - button /element that opens modal
  * @param  {jQuery} $modal
  * @param  {jQuery} $content
  * @param  {jQuery} $overlay
  * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
  * @param  {Object} [options={}] - Any options provided upon initialization
  * @param  {Function} deregister - teardown method from register jquery plugin;
 */
const escapeModal = (
  evt,
  $el,
  $modal,
  $content,
  $overlay,
  typeAdjust,
  options,
) => {
  if (Keyboard.parseKey(evt) === Keyboard.keys.ESCAPE) {
    closeModal(
      evt,
      $el,
      $modal,
      $content,
      $overlay,
      typeAdjust,
      options,
    );
  }
};
/**
 * Handles the click event when modal trigger/ button is clicked
 * @param  {jQuery} $el - control
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @param  {event} evt
 * @param  {Function} deregister - teardown method from register jquery plugin;
 */
const openModalEventHandler = (
  $el,
  $modal,
  $content,
  $overlay,
  typeAdjust,
  options = {},
  evt,
  deregister,
) => {
  if (evt) {
    evt.preventDefault();
  }
  closeAllModals();
  $content
    // To ensure the event isn't get bound multiple times
    .off('keydown.modalEscape')
    // escape event to close modal
    .on('keydown.modalEscape', event =>
      escapeModal(
        event,
        $el,
        $modal,
        $content,
        $overlay,
        typeAdjust,
        options,
      ),
    )
    .data('modalEscape', true)
    // Add listener modal content for modalCloseClass to close modal
    .on('mousedown.modal', '.js-modal-close', event =>
      closeModal(
        event,
        $el,
        $modal,
        $content,
        $overlay,
        typeAdjust,
        options,
      ),
    )
    .on('keydown', '.js-modal-close', (e) => {
      if (e.type === 'keydown' && e.key === 'Enter') {
        closeModal(
          e,
          $el,
          $modal,
          $content,
          $overlay,
          typeAdjust,
          options,
        );
      }
    })
    // Prevents href getting added to end of URL
    .on('click.modal', '.js-modal-close', event => event.preventDefault())
    // Stop the clicks from bubbling up
    .on('mousedown.modal', event => event.stopPropagation());

  if (options.overlayClose) {
    $modal
      // Add listener to outside of the modal if set true
      .on('mousedown.modal', event =>
        closeModal(
          event,
          $el,
          $modal,
          $content,
          $overlay,
          typeAdjust,
          options,
        ),
      );
  }
  // If url is passed then attempt to load ajax.
  if (options.url) {
    options.onLoad({ target: $el, options });
    addModalModifiers($modal, options.type);
    if (options.type !== 'notification') {
      addActiveClass($overlay);
    }
    $.ajax({
      url: options.url,
      method: 'POST',
      cache: options.cache,
      data: options.data,
    }).done((content) => {
      $content.prepend(content);
      openModal(
        $el,
        $modal,
        $content,
        $overlay,
        typeAdjust,
        options,
        deregister,
      );
    });
    // Not dynamic, open inline modal instead.
  } else {
    openModal($el, $modal, $content, $overlay, typeAdjust, options, deregister);
  }
};
/**
 * Applies any sort of event handlers for the modal.
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const setEventListeners = (
  $el,
  $modal,
  $content,
  $overlay,
  typeAdjust,
  options = {},
) => {
  // Add escape key down feature
  // Add listener to button to open modal
  if (!options.triggerOpen) {
    $($el).on('click.modal', evt =>
      openModalEventHandler(
        $el,
        $modal,
        $content,
        $overlay,
        typeAdjust,
        options,
        evt,
      ),
    );
  }
};
/**
 * Remove any sort of event handlers for the modal.
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 */
const removeEventListeners = ($el, $modal, $content) => {
  $el.off('click.modal');
  $content.off('mousedown.modal').off('keydown.modalEscape');
  $modal.off('mousedown.modal');
};
/**
 * If triggerOpen option is set true open modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {jQuery} $overlay
 * @param  {Object} [typeAdjust={}] - Modal Settings define by modal type
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const checkTriggerOpen = (
  $el,
  $modal,
  $content,
  $overlay,
  typeAdjust,
  options = {},
) => {
  if (options.triggerOpen) {
    openModalEventHandler(
      $el,
      $modal,
      $content,
      $overlay,
      typeAdjust,
      options,
      false,
    );
  }
};
/**
 * @param  {jQuery} $el - button /element that opens modal
 * @param  {jQuery} $modal
 * @param  {jQuery} $content
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @param  {Function} deregister - teardown method from register jquery plugin;
*/
const destroy = ($el, $modal, $content, options = {}, deregister) => {
  removeEventListeners($el, $modal, $content);
  removeActiveClass($('.modal-overlay'), $('.modal'));
  removeModalModifiers($modal);
  deregister();
  unlockBody();
  if (options.url) {
    $modal.remove();
  }
};
/* Add Close Button to modal
 * @param  {jQuery} $content
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const addCloseButton = ($content, options = {}) => {
  if (
    options.closeButton &&
    !$content.find('.modal__close').length &&
    options.type !== 'notification'
  ) {
    $content.append(`
      <a href="#" class="u-margin-a--sm js-modal-close modal__close">
        <span class="icon icon--sm icon--x">
          <svg viewBox="0 0 16 16" focusable="false" aria-hidden="true">
            <use xlink:href="#icon-x"></use>
          </svg>
          <div class="u-screen-reader">Close Modal</div>
        </span>
      </a>
    `);
  } else if (!options.closeButton) {
    $content.find('.modal__close').remove();
  }
};
/**
 * A11y: Set ARIA attributes on modal trigger element
 * @param  {jQuery} $el - button /element that opens modal
 */
const setA11y = ($el) => {
  $el.attr('aria-haspopup', 'dialog');

  if ($el.is('a')) {
    $el.attr('role', 'button');
  } else if ($el.is('button')) {
    $el.attr('type', 'button');
  }
};
/**
 * Initializes modal.
 * @param {HTMLElement} el - button /element that opens modal
 * @param {Object} options - Options used for wiring up the modal
 * @param {Object} deregister - Performs a deregisteration on the plugin
 * @see DEFAULTS
 */
export default function modal(el, options = {}, { deregister } = noopActions) {
  const $el = options.triggerElement ? $(options.triggerElement) : $(el);
  const opts = mergeOptions($el, options);
  if (opts) {
    const $modal = setModal($el, opts);
    const $overlay = appendModalOverlay();
    const $content = wrapModalContent($modal, opts);
    const typeAdjust = adjustToModalType($modal, opts);
    setA11y($el);
    addCloseButton($content, opts);
    setEventListeners(
      $el,
      $modal,
      $content,
      $overlay,
      typeAdjust,
      opts,
      deregister,
    );
    checkTriggerOpen(
      $el,
      $modal,
      $content,
      $overlay,
      typeAdjust,
      opts,
      deregister,
    );
    return {
      destroy: () => destroy($el, $modal, $content, opts, deregister),
    };
  }
  return false;
}
/**
 * Utility Function for Modal
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
$.modal = (options) => {
  $('<div/>').modal(options);
};
/**
 * Utility Function for Closing Modal
 */
$.modal.close = () => {
  closeAllModals();
};
/**
 * Utility Function for accessibility
 */
$.modal.a11y = () => {
  const $els = $('.js-modal-utility');

  $els.each(function applyA11y() {
    setA11y($(this));
  });
};
registerJQueryPlugin(PLUGIN_NAME, modal);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/modal.js